<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useFormItem, Notification, type FileItem } from '@arco-design/web-vue';
import type { ListType } from '@arco-design/web-vue/es/upload/interfaces';
import useLoadingCount from '@/hooks/loading-count';
import { getToken } from '@/utils/auth';
import { UPLOAD_ACTION_URL } from './constants';
import { checkFileSize } from './utils';

defineOptions({
  name: 'OpenUpload',
});

interface FileInfo {
  mediaId: string;
  url: string;
}

interface FileResponseData {
  mediaId?: string;
  url?: string;
}

const {
  limit = 1,
  accept = 'image/*',
  maxFileSize = 1000 * 1000 * 10,
  listType = 'picture',
  simple = false,
  url: propUrl = '',
  usage = 'other',
} = defineProps<{
  limit?: number;
  accept?: string;
  maxFileSize?: number;
  listType?: ListType;
  simple?: boolean;
  usage?: 'cover' | 'avatar' | 'document' | 'content' | 'other';
  // 只读回显 URL，与 modelValue 的 mediaId 按 index 对齐。父组件负责保证对应关系。
  // 不提供时该项不回显（仅展示占位），不影响 modelValue 语义。
  url?: string | string[];
}>();

// 对外 modelValue：单文件为 mediaId(string)，多文件为 mediaId 数组
const modelValue = defineModel<string | string[]>({
  default: '',
});

const emit = defineEmits<{
  success: [payload: FileInfo & { fileItem: FileItem }];
  error: [fileItem: FileItem];
}>();

const error = ref(false);
const fileList = ref<FileItem[]>([]);
const loading = ref(false);
const uploadData = ref({});
const uploadHeaders = ref({});

const { increase, decrease } = useLoadingCount();
const { mergedDisabled, eventHandlers, formItemCtx } = useFormItem({ error });

const getUid = () => String(+new Date()) + String(Math.floor(Math.random() * 9999));

/**
 * 从上传接口的响应中解析出 mediaId 与 url。
 */
const parseUploadedFile = (response: unknown): FileInfo | null => {
  const data = (response as { success?: boolean; data?: FileResponseData })?.data;

  if (!data?.mediaId || !data?.url) {
    return null;
  }

  return { mediaId: data.mediaId, url: data.url };
};

// 未处于 AForm 上下文时 formItemCtx 为空对象，updateValidateState 不存在，需守卫
const updateValidateState = (status: 'validating' | 'success' | 'error', message = ''): void => {
  formItemCtx?.updateValidateState?.('upload', { status, message });
};

// 规整化为数组形式
const toArray = (value: string | string[]): string[] => {
  if (Array.isArray(value)) {
    return value;
  }

  return value ? [value] : [];
};

// 由 fileList 派生 modelValue（顺序与 fileList 一致）
const syncModelValueFromList = () => {
  if (simple) {
    return;
  }

  const ids = fileList.value
    .map(item => item.name)
    .filter(Boolean) as string[];

  if (limit === 1) {
    modelValue.value = ids[0] ?? '';
  }
  else {
    modelValue.value = ids;
  }
};

// 回显：按 mediaId 数组与 propUrl 数组（index 对齐）纯同步构造 fileList
const buildFileListFromModel = (ids: string[], urlList: string[]) => {
  if (simple || ids.length === 0) {
    fileList.value = [];

    return;
  }

  // 幂等 diff：期望集与当前集（含顺序）一致则直接返回
  const currentIds = fileList.value.map(item => item.name).filter(Boolean) as string[];

  if (currentIds.length === ids.length && currentIds.every((id, i) => id === ids[i])) {
    // 顺序与 id 一致，但仍可能需要回填 url
    const updated = fileList.value.map((item, i) => {
      const url = urlList[i] ?? '';

      // 不覆盖上传新增项已有的 url（来自响应，prop 可能尚未更新）
      if (item.url || !url) {
        return item;
      }

      return { ...item, url };
    });

    // 仅当有变化时才替换，避免多余触发
    if (updated.some((item, i) => item !== fileList.value[i])) {
      fileList.value = updated;
    }

    return;
  }

  // 按期望顺序重建 fileList，复用已存在的 uid（命中则保留 url，不丢失上传新增项的 url）
  fileList.value = ids.map((mediaId, i) => {
    const existing = fileList.value.find(item => item.name === mediaId);
    const uid = existing?.uid ?? getUid();
    const url = existing?.url || urlList[i] || '';

    return {
      uid,
      name: mediaId,
      url,
      status: 'done' as const,
    };
  });
};

// 初始化
buildFileListFromModel(toArray(modelValue.value), toArray(propUrl));

// 外部 → 内部：modelValue 变化时回显
watch(modelValue, (value, oldValue) => {
  if (value === oldValue) {
    return;
  }

  buildFileListFromModel(toArray(value), toArray(propUrl));
});

// 外部 → 内部：propUrl 变化时仅回填空 url 项，不覆盖已有 url（上传新增项）
watch(() => propUrl, (value) => {
  const urlList = toArray(value);

  buildFileListFromModel(toArray(modelValue.value), urlList);
});

// 内部 → 外部：fileList 变化（用户删除/重排）时同步 modelValue
watch(fileList, (value) => {
  if (simple) {
    return;
  }

  syncModelValueFromList();

  // 上传中不触发表单校验，避免 Arco 在上传过程中误报"未通过"
  const uploading = value.some(item => item.status === 'uploading');

  if (!uploading) {
    eventHandlers.value?.onChange?.();
  }
}, { deep: true });

const onBeforeUpload = async (file: File) => {
  if (!checkFileSize(file, maxFileSize)) {
    return false;
  }

  loading.value = true;
  increase();

  uploadData.value = {
    usage,
  };

  uploadHeaders.value = {
    Authorization: `Bearer ${getToken()}`,
  };

  // 标记表单字段为"校验中"，避免上传过程被必填校验误判
  updateValidateState('validating');

  return file;
};

const onSuccess = (fileItem: FileItem) => {
  loading.value = false;
  decrease();

  const file = parseUploadedFile(fileItem.response);

  if (!file) {
    Notification.error({
      content: '上传响应解析失败！',
      duration: 10 * 1000,
    });

    updateValidateState('error', '上传响应解析失败');

    emit('error', fileItem);

    return;
  }

  // Arco 传入的 fileItem 就是 fileList 中的同一引用（fileMap.get(uid)），直接 mutate 即可触发响应式。
  // 不用 findIndex 替换，避免与 Arco 内部 watch(fileList) 的重建产生时序冲突。
  fileItem.url = file.url;
  fileItem.name = file.mediaId;

  // 显式触发一次 fileList 更新，确保视图刷新
  fileList.value = [...fileList.value];

  syncModelValueFromList();

  eventHandlers.value?.onChange?.();

  updateValidateState('success');

  emit('success', { ...file, fileItem });
};

const onError = (fileItem: FileItem) => {
  Notification.error({
    content: '上传失败！',
    duration: 10 * 1000,
  });

  // 上传失败的项从列表移除，避免残留无效占位
  const uid = fileItem.uid ?? '';

  fileList.value = fileList.value.filter(item => item.uid !== uid);

  syncModelValueFromList();

  loading.value = false;
  decrease();

  updateValidateState('error', '上传失败');

  emit('error', fileItem);
};
</script>

<template>
  <AUpload
    class="open-upload"
    :class="{ 'open-upload-disabled': mergedDisabled }"
    v-model:file-list="fileList"
    :action="UPLOAD_ACTION_URL"
    :data="uploadData"
    :headers="uploadHeaders"
    :limit="limit"
    :accept="accept"
    :list-type="listType"
    :show-link="false"
    :disabled="mergedDisabled"
    :show-file-list="!simple"
    :show-upload-button="{ showOnExceedLimit: simple }"
    @before-upload="onBeforeUpload"
    @success="onSuccess"
    @error="onError"
  >
    <template #upload-button>
      <template v-if="$slots['upload-button']">
        <slot :loading="loading" name="upload-button"></slot>
      </template>
      <template v-else>
        <AButton>
          <template #icon>
            <IconUpload />
          </template>
          点击上传
        </AButton>
      </template>
    </template>

    <template #image="{ fileItem, index }">
      <AImage v-if="fileItem.url" :src="fileItem.url" :key="index" show-loader />
      <div
        v-else
        :key="`placeholder-${index}`"
        class="flex min-h-[100px] w-full flex-col items-center justify-center gap-1.5 rounded border border-dashed border-[var(--color-border-2)] bg-[var(--color-fill-1)] text-xs text-[var(--color-text-3)]"
      >
        <IconImage class="text-[28px] text-[var(--color-text-4)]" />
        <span>暂无预览</span>
      </div>
    </template>

    <template #file-name="{ fileItem }">
      <ALink v-if="fileItem.url" :href="fileItem.url" :hoverable="false" target="_blank">{{ decodeURIComponent(fileItem.url) }}</ALink>
      <span v-else class="text-[var(--color-text-3)]">暂无预览</span>
    </template>

    <template #retry-icon>
    </template>
  </AUpload>
</template>

<style lang="scss" scoped>
.open-upload {
  &:deep() {
    .arco-upload-list.arco-upload-list-type-picture {
      .arco-upload-list-item {
        position: relative;
        display: block;
        margin-top: 10px;

        .arco-upload-list-item-content {
          display: block;
          padding: 0;
          background-color: transparent;
          border-radius: 0;

          .arco-upload-list-item-thumbnail {
            display: block;
            width: auto;
            height: auto;
            margin-right: 0;

            .arco-image:not(.arco-image-loading-error) {
              cursor: pointer;
            }

            img {
              display: block;
              // width: auto;
              max-width: 100%;
              height: auto;
              max-height: 150px;
            }
          }

          .arco-upload-list-item-name {
            display: none;
          }

          .arco-upload-progress {
            display: flex;
            align-items: center;
            height: 20px;
            margin-top: 5px;
            margin-left: 0;

            .arco-upload-icon.arco-upload-icon-success {
              cursor: default;
            }
          }
        }

        .arco-upload-list-item-operation {
          position: absolute;
          bottom: 3px;
          left: 24px;
          margin-left: 0;
        }
      }
    }
  }

  &.open-upload-disabled {
    &:deep() {
      .arco-upload-list-item-operation {
        display: none;
      }
    }
  }
}
</style>
